import type { FormEvent } from 'react';
import { Button, Card, CardBody, FormField, Input, Select } from '@/shared/components';
import type { ParkingPriceUnit } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';

export interface ParkingDraft {
  label: string;
  price: string;
  currency: string;
  priceUnit: ParkingPriceUnit;
  isActive: boolean;
  assignedPropertyIds: string[];
}

interface Props {
  draft: ParkingDraft;
  editing: boolean;
  properties: { id: string; name: string }[];
  saving: boolean;
  /** Kleinster Betrag einer Online-Zahlung in der Währung des Hofs — vom Server geladen. */
  minimumCharge?: MinimumCharge;
  onChange: (draft: ParkingDraft) => void;
  onSubmit: (draft: ParkingDraft) => void;
  onCancel: () => void;
}

export interface MinimumCharge {
  amount: number;
  currency: string;
}

export const emptyParkingDraft = (): ParkingDraft => ({
  label: '',
  price: '',
  currency: 'EUR',
  priceUnit: 'PerNight',
  isActive: true,
  assignedPropertyIds: [],
});

export function ObjectParkingForm(props: Props) {
  const { draft, editing, properties, saving, onChange, onSubmit, onCancel } = props;
  const patch = (value: Partial<ParkingDraft>) => onChange({ ...draft, ...value });
  const toggle = (id: string) =>
    patch({
      assignedPropertyIds: draft.assignedPropertyIds.includes(id)
        ? draft.assignedPropertyIds.filter((value) => value !== id)
        : [...draft.assignedPropertyIds, id],
    });
  const amount = Number(draft.price);
  const invalid =
    !draft.label.trim() ||
    !draft.price.trim() ||
    !Number.isFinite(amount) ||
    amount < 0 ||
    draft.currency.trim().length !== 3 ||
    !draft.assignedPropertyIds.length;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!invalid) onSubmit(draft);
  };
  return (
    <Card variant="alt">
      <CardBody>
        <form onSubmit={submit} className="grid gap-3 md:grid-cols-3">
          <FormField label="Bezeichnung" required>
            <Input
              value={draft.label}
              maxLength={40}
              onChange={(event) => patch({ label: event.target.value })}
            />
          </FormField>
          <FormField label="Preis" required hint={priceHint(draft, props.minimumCharge)}>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={draft.price}
              onChange={(event) => patch({ price: event.target.value })}
            />
          </FormField>
          <FormField label="Währung" required>
            <Input
              value={draft.currency}
              maxLength={3}
              onChange={(event) => patch({ currency: event.target.value.toUpperCase() })}
            />
          </FormField>
          <FormField label="Abrechnung">
            <Select
              value={draft.priceUnit}
              onChange={(event) => patch({ priceUnit: event.target.value as ParkingPriceUnit })}
            >
              <option value="PerNight">pro Nacht</option>
              <option value="PerStay">pro Aufenthalt</option>
            </Select>
          </FormField>
          <fieldset className="md:col-span-2">
            <legend className="text-ink-soft text-sm">Apartment-Zuweisung</legend>
            <div className="mt-2 flex flex-wrap gap-3">
              {properties.map((property) => (
                <label key={property.id} className="text-ink-soft flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={draft.assignedPropertyIds.includes(property.id)}
                    onChange={() => toggle(property.id)}
                  />
                  {property.name}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="text-ink-soft flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(event) => patch({ isActive: event.target.checked })}
            />
            Für Gäste buchbar
          </label>
          <div className="flex justify-end gap-2 md:col-span-2">
            {editing && (
              <Button type="button" onClick={onCancel}>
                Abbrechen
              </Button>
            )}
            <Button type="submit" variant="brand" loading={saving} disabled={invalid}>
              {editing ? 'Änderungen speichern' : 'Parkplatz anlegen'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

/**
 * Was der Preis für Gäste bedeutet. Kostenlos geht ohne Stripe; ein Betrag über null, aber unter
 * dem Mindestbetrag lässt sich weder bezahlen noch verschenken — der Server lehnt ihn ab. Der
 * Mindestbetrag gilt nur für seine Währung; bei einer anderen schweigt der Hinweis lieber.
 */
export function priceHint(draft: ParkingDraft, minimum?: MinimumCharge): string | undefined {
  const amount = Number(draft.price);
  if (!draft.price.trim() || !Number.isFinite(amount) || amount < 0) return undefined;
  if (amount === 0) return 'Kostenlos – Gäste reservieren ohne Zahlung, auch ohne Stripe.';
  if (!minimum || draft.currency.trim().toUpperCase() !== minimum.currency) return undefined;
  if (amount >= minimum.amount) return undefined;
  return `Unter dem Mindestbetrag für Online-Zahlungen (${formatMoney(minimum.amount, minimum.currency)}): Gäste können nur buchen, wenn ihre Auswahl ihn erreicht.`;
}
